import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTranslation } from 'react-i18next';
import { organizationService } from '@services/organizationService';
import {
  auditService,
  type BenchmarkResponse,
  type BenchmarkConformanceLevel,
} from '@services/auditService';
import ScrollableTableContainer from '@components/common/ScrollableTableContainer';
import { toDisplayError } from '@utils/apiError';

type BaselineChoice = 'none' | 'auto' | 'industry';

const LEVELS: BenchmarkConformanceLevel[] = [
  'supports',
  'partially_supports',
  'does_not_support',
  'not_applicable',
  'not_evaluated',
];

const PRINCIPLES = ['Perceivable', 'Operable', 'Understandable', 'Robust'];

function formatPassRate(rate: number | null | undefined): string {
  if (rate === null || rate === undefined) return '—';
  return `${Math.round(rate * 1000) / 10}%`;
}

function passRateDelta(current: number | null, baseline: number | null): number | null {
  if (current === null || baseline === null) return null;
  return Math.round((current - baseline) * 1000) / 10; // percentage points
}

/**
 * REV-216: audit benchmark view. Lets a consultant compare an audit's
 * pass_rate + by-principle breakdown against either a prior audit for the
 * same context or a platform-wide industry aggregate.
 */
export default function AuditBenchmarkPage() {
  const { t } = useTranslation();
  const { slug, auditId } = useParams<{ slug: string; auditId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [report, setReport] = useState<BenchmarkResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [baseline, setBaseline] = useState<BaselineChoice>(() => {
    const q = searchParams.get('baseline');
    if (q === 'auto' || q === 'industry') return q;
    return 'none';
  });

  const load = useCallback(async () => {
    if (!slug || !auditId) return;
    setLoading(true);
    setError(null);
    try {
      const org = await organizationService.getBySlug(slug);
      const res = await auditService.getBenchmark(
        org.id,
        auditId,
        baseline === 'none' ? null : baseline
      );
      setReport(res);
    } catch (err) {
      setError(toDisplayError(err, t('benchmark.errors.fetchFailed', 'Failed to load benchmark')));
    } finally {
      setLoading(false);
    }
  }, [slug, auditId, baseline, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleBaselineChange = (e: SelectChangeEvent) => {
    const v = e.target.value as BaselineChoice;
    setBaseline(v);
    // Reflect the choice in the URL so it survives refresh and share.
    const next = new URLSearchParams(searchParams);
    if (v === 'none') next.delete('baseline');
    else next.set('baseline', v);
    setSearchParams(next, { replace: true });
  };

  const passDelta = useMemo(() => {
    if (!report || !report.baseline) return null;
    return passRateDelta(report.pass_rate, report.baseline.pass_rate);
  }, [report]);

  if (loading && !report) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !report) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          sx={{ mt: 2 }}
          startIcon={<ArrowBackIcon />}
          component={RouterLink}
          to={`/orgs/${slug}/audits/${auditId}`}
        >
          {t('benchmark.backToAudit', 'Back to audit')}
        </Button>
      </Box>
    );
  }

  if (!report) return null;

  return (
    <Box sx={{ p: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        component={RouterLink}
        to={`/orgs/${slug}/audits/${auditId}`}
        sx={{ mb: 2 }}
      >
        {t('benchmark.backToAudit', 'Back to audit')}
      </Button>

      <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
        {t('benchmark.title', 'Audit Benchmark')}
      </Typography>
      <Typography variant="h6" color="text.secondary" gutterBottom>
        {t('benchmark.subtitle', '{{name}} — WCAG {{version}} {{level}}', {
          name: report.audit.name || t('benchmark.untitled', 'Untitled audit'),
          version: report.audit.wcag_version,
          level: report.audit.target_level,
        })}
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-end">
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel id="benchmark-baseline-label">
              {t('benchmark.baseline', 'Compare against')}
            </InputLabel>
            <Select
              labelId="benchmark-baseline-label"
              id="benchmark-baseline"
              value={baseline}
              label={t('benchmark.baseline', 'Compare against')}
              onChange={handleBaselineChange}
            >
              <MenuItem value="none">
                <em>{t('benchmark.baselineNone', 'No comparison')}</em>
              </MenuItem>
              <MenuItem value="auto">
                {t('benchmark.baselineAuto', 'Previous audit (same context)')}
              </MenuItem>
              <MenuItem value="industry">
                {t('benchmark.baselineIndustry', 'Industry average')}
              </MenuItem>
            </Select>
          </FormControl>
          {loading && <CircularProgress size={20} aria-label={t('common.loading', 'Loading...')} />}
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: report.baseline ? 6 : 12 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              {t('benchmark.current.heading', 'Current audit')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('benchmark.passRate', 'Pass rate')}
            </Typography>
            <Typography variant="h3">{formatPassRate(report.pass_rate)}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {t('benchmark.passRateHelp', {
                defaultValue: '{{supports}} of {{total}} applicable criteria',
                supports: report.counts_by_level.supports,
                total: report.counts_by_level.total_applicable,
              })}
            </Typography>
          </Paper>
        </Grid>

        {report.baseline && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {report.baseline.kind === 'industry'
                    ? t('benchmark.baselines.industry', 'Industry average')
                    : t('benchmark.baselines.previous', 'Previous audit')}
                </Typography>
                {report.baseline.kind === 'previous_audit' && report.baseline.audit_name && (
                  <Chip size="small" label={report.baseline.audit_name} />
                )}
                {report.baseline.kind === 'industry' && report.baseline.sample_size && (
                  <Chip
                    size="small"
                    label={t('benchmark.sample', {
                      defaultValue: '{{n}} evaluations',
                      n: report.baseline.sample_size,
                    })}
                  />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {t('benchmark.passRate', 'Pass rate')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
                <Typography variant="h3">{formatPassRate(report.baseline.pass_rate)}</Typography>
                {passDelta !== null && (
                  <Typography
                    variant="h6"
                    color={
                      passDelta > 0
                        ? 'success.main'
                        : passDelta < 0
                          ? 'error.main'
                          : 'text.secondary'
                    }
                    aria-label={t('benchmark.passDeltaAria', {
                      defaultValue: 'Change: {{delta}} percentage points',
                      delta: passDelta,
                    })}
                  >
                    {t('benchmark.deltaPoints', '{{delta}} pp', {
                      delta: passDelta > 0 ? `+${passDelta}` : passDelta,
                    })}
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>

      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {t('benchmark.byPrinciple', 'By WCAG principle')}
          </Typography>
        </Box>
        <Divider />
        <ScrollableTableContainer>
          <Table size="small" aria-label={t('benchmark.principleTable', 'By-principle counts')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('benchmark.principle', 'Principle')}</TableCell>
                {LEVELS.map((l) => (
                  <TableCell key={l} align="right">
                    {t(`benchmark.levels.${l}`, l.replace(/_/g, ' '))}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {PRINCIPLES.map((p) => {
                const row = report.by_principle[p];
                if (!row) return null;
                return (
                  <TableRow key={p}>
                    <TableCell>{p}</TableCell>
                    {LEVELS.map((l) => (
                      <TableCell key={l} align="right">
                        {row[l]}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </ScrollableTableContainer>
      </Paper>

      {report.baseline && report.baseline.deltas && (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom color="success.main">
                {t('benchmark.improved', {
                  defaultValue: 'Improved ({{n}})',
                  n: report.baseline.deltas.improved.length,
                })}
              </Typography>
              {report.baseline.deltas.improved.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('benchmark.noImproved', 'No criteria improved.')}
                </Typography>
              ) : (
                <Stack spacing={0.5}>
                  {report.baseline.deltas.improved.map((d) => (
                    <Typography key={d.wcag_sc_id} variant="body2">
                      <strong>{d.wcag_sc_id}</strong>: {d.from.replace(/_/g, ' ')} →{' '}
                      {d.to.replace(/_/g, ' ')}
                    </Typography>
                  ))}
                </Stack>
              )}
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom color="error.main">
                {t('benchmark.regressed', {
                  defaultValue: 'Regressed ({{n}})',
                  n: report.baseline.deltas.regressed.length,
                })}
              </Typography>
              {report.baseline.deltas.regressed.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('benchmark.noRegressed', 'No criteria regressed.')}
                </Typography>
              ) : (
                <Stack spacing={0.5}>
                  {report.baseline.deltas.regressed.map((d) => (
                    <Typography key={d.wcag_sc_id} variant="body2">
                      <strong>{d.wcag_sc_id}</strong>: {d.from.replace(/_/g, ' ')} →{' '}
                      {d.to.replace(/_/g, ' ')}
                    </Typography>
                  ))}
                </Stack>
              )}
            </Paper>
          </Grid>
          <Grid size={12}>
            <Typography variant="caption" color="text.secondary">
              {t('benchmark.unchanged', {
                defaultValue: '{{n}} criteria unchanged.',
                n: report.baseline.deltas.unchanged,
              })}
            </Typography>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
